import React, { useEffect, useState } from 'react';
import { DotLottieReact } from '@lottiefiles/dotlottie-react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Button,
  Fade,
  Zoom,
  Card,
  CardContent,
  IconButton,
  Collapse,
  Tooltip,
  Divider,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Link,
} from '@mui/material';
import SecurityIcon from '@mui/icons-material/Security';
import EngineeringIcon from '@mui/icons-material/Engineering';
import PrecisionManufacturingIcon from '@mui/icons-material/PrecisionManufacturing';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import HandshakeIcon from '@mui/icons-material/Handshake';
import SpeedIcon from '@mui/icons-material/Speed';
import BarChartIcon from '@mui/icons-material/BarChart';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import SettingsIcon from '@mui/icons-material/Settings';
import VerifiedIcon from '@mui/icons-material/Verified';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import certificate from "../assets/certificate.png"
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import AOS from 'aos';
import 'aos/dist/aos.css';

const themeColors = {
  primary: '#1a472a',
  secondary: '#4caf50',
  text: '#333333',
  background: '#f5f5f5',
  white: '#ffffff',
  accent: '#ffd700'
};

const capabilities = [
  {
    icon: <SecurityIcon sx={{ color: themeColors.primary, fontSize: 40 }} />,
    title: 'Quality Assurance',
    description: 'Committed to delivering the highest quality precision components'
  },
  {
    icon: <SettingsIcon sx={{ color: themeColors.primary, fontSize: 40 }} />,
    title: 'Advanced Machinery',
    description: 'State-of-the-art equipment for precision manufacturing'
  },
  {
    icon: <EngineeringIcon sx={{ color: themeColors.primary, fontSize: 40 }} />,
    title: 'Expert Engineering',
    description: 'Team of skilled engineers and technical experts'
  },
  {
    icon: <PrecisionManufacturingIcon sx={{ color: themeColors.primary, fontSize: 40 }} />,
    title: 'Precision Manufacturing',
    description: 'High-precision manufacturing processes and techniques'
  },
  {
    icon: <WorkspacePremiumIcon sx={{ color: themeColors.primary, fontSize: 40 }} />,
    title: 'Industry Standards',
    description: 'Adherence to international quality standards'
  },
  {
    icon: <HandshakeIcon sx={{ color: themeColors.primary, fontSize: 40 }} />,
    title: 'Customer Focus',
    description: 'Strong relationships and dedicated customer support'
  }
];

const About = () => {
  const [expandedCard, setExpandedCard] = useState(null);
  const [isHovered, setIsHovered] = useState(null);

  useEffect(() => {
    AOS.init({
      duration: 1000,
      once: true,
    });
  }, []);

  const handleExpandClick = (index) => {
    setExpandedCard(expandedCard === index ? null : index);
  };

  return (
    <Box sx={{ bgcolor: themeColors.background, py: 8, overflow: 'hidden' }}>
      <Container maxWidth="lg">
        {/* Header Section with Enhanced Parallax Effect and Lottie Animation */}
        <Box 
          sx={{ 
            textAlign: 'center', 
            mb: 8,
            position: 'relative',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: -20,
              left: -20,
              right: -20,
              bottom: -20,
              background: `radial-gradient(circle, ${themeColors.secondary}15 0%, transparent 70%)`,
              zIndex: 0,
              transform: 'translateY(0)',
              transition: 'transform 0.3s ease-in-out',
            },
            '&:hover::before': {
              transform: 'translateY(-10px)',
            }
          }}
          data-aos="fade-down"
        >
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography
                variant="h2"
                sx={{
                  color: themeColors.primary,
                  fontWeight: 'bold',
                  mb: 2,
                  position: 'relative',
                  textShadow: '2px 2px 4px rgba(0,0,0,0.1)',
                  '&:after': {
                    content: '""',
                    position: 'absolute',
                    bottom: -16,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: 100,
                    height: 4,
                    bgcolor: themeColors.accent,
                    borderRadius: '2px',
                  }
                }}
              >
                About Us
              </Typography>
              <Fade in timeout={1000}>
                <Typography 
                  variant="h6" 
                  color="text.secondary" 
                  sx={{ 
                    mt: 4, 
                    maxWidth: 800, 
                    mx: 'auto',
                    fontSize: '1.2rem',
                    lineHeight: 1.8,
                  }}
                >
                  Vibhuti Industries India Private Limited - Engineering Excellence Through Innovation
                </Typography>
              </Fade>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box 
                sx={{ 
                  maxWidth: '100%',
                  height: '300px',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  position: 'relative',
                  '&:hover': {
                    transform: 'scale(1.05)',
                    transition: 'transform 0.3s ease-in-out'
                  }
                }}
              >
                <DotLottieReact
                  src="https://lottie.host/4731a890-d627-4caa-8579-5487e627ca65/vpBuKwBqNt.lottie"
                  loop
                  autoplay
                  style={{ width: '100%', height: '100%' }}
                />
              </Box>
            </Grid>
          </Grid>
        </Box>

        {/* ISO Certification Section */}
        <Paper
          elevation={4}
          sx={{
            p: 4,
            mb: 6,
            background: `linear-gradient(135deg, ${themeColors.white} 0%, ${themeColors.background} 100%)`,
            borderRadius: 2,
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              background: `radial-gradient(circle at top right, ${themeColors.secondary}15, transparent 70%)`,
              zIndex: 0
            }
          }}
          data-aos="fade-up"
        >
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={6}>
              <Box
                component="img"
                src={certificate}
                alt="ISO 9001:2015 Certificate"
                sx={{
                  width: '80%',
                  maxWidth: '400px',
                  height: 'auto',
                  borderRadius: 2,
                  boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                  transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                  cursor: 'pointer',
                  display: 'block',
                  margin: '0 auto',
                  '&:hover': {
                    transform: 'scale(1.02)',
                    boxShadow: '0 8px 30px rgba(0,0,0,0.15)'
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography
                variant="h4"
                sx={{
                  color: themeColors.primary,
                  fontWeight: 'bold',
                  mb: 3,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2
                }}
              >
                <VerifiedIcon sx={{ color: themeColors.secondary, fontSize: 40 }} />
                ISO 9001:2015 Certified
              </Typography>

              <List sx={{ mt: 2 }}>
                <ListItem>
                  <ListItemIcon>
                    <SpeedIcon sx={{ color: themeColors.primary }} />
                  </ListItemIcon>
                  <ListItemText
                    primary="Certificate Number"
                    secondary="QMS/BC5D/1124"
                    primaryTypographyProps={{ fontWeight: 'bold' }}
                  />
                </ListItem>

                <ListItem>
                  <ListItemIcon>
                    <BarChartIcon sx={{ color: themeColors.primary }} />
                  </ListItemIcon>
                  <ListItemText
                    primary="Scope"
                    secondary="Manufacturing of Precision Turned Components for Automotive, Aerospace Engine Parts & General Industries"
                    primaryTypographyProps={{ fontWeight: 'bold' }}
                  />
                </ListItem>

                <ListItem>
                  <ListItemIcon>
                    <LocationOnIcon sx={{ color: themeColors.primary }} />
                  </ListItemIcon>
                  <ListItemText
                    primary="Location"
                    secondary="E-143, Sector-63, Noida, Gautam Buddha Nagar, Uttar Pradesh - 201301, India"
                    primaryTypographyProps={{ fontWeight: 'bold' }}
                  />
                </ListItem>
              </List>
            </Grid>
          </Grid>
        </Paper>

        {/* Contact Information Section */}
        <Box sx={{ mb: 6 }} data-aos="fade-up">
          <Typography variant="h4" sx={{ mb: 3, color: themeColors.primary, fontWeight: 'bold' }}>
            Get in Touch
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card 
                sx={{ 
                  height: '100%',
                  transition: 'transform 0.3s ease',
                  '&:hover': {
                    transform: 'scale(1.02)',
                  }
                }}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <EmailIcon sx={{ color: themeColors.primary, mr: 1 }} />
                    <Typography variant="h6">Email</Typography>
                  </Box>
                  <Link 
                    href="mailto:office@thevibuti.com"
                    sx={{ 
                      color: 'text.secondary',
                      textDecoration: 'none',
                      '&:hover': {
                        textDecoration: 'underline',
                        color: themeColors.primary
                      }
                    }}
                  >
                    office@thevibuti.com
                  </Link>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card 
                sx={{ 
                  height: '100%',
                  transition: 'transform 0.3s ease',
                  '&:hover': {
                    transform: 'scale(1.02)',
                  }
                }}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <PhoneIcon sx={{ color: themeColors.primary, mr: 1 }} />
                    <Typography variant="h6">Phone</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Link 
                      href="tel:+919999922657"
                      sx={{ 
                        color: 'text.secondary',
                        textDecoration: 'none',
                        '&:hover': {
                          textDecoration: 'underline',
                          color: themeColors.primary
                        }
                      }}
                    >
                      +91-9999922657
                    </Link>
                    <Link 
                      href="tel:+919667674397"
                      sx={{ 
                        color: 'text.secondary',
                        textDecoration: 'none',
                        '&:hover': {
                          textDecoration: 'underline',
                          color: themeColors.primary
                        }
                      }}
                    >
                      +91-9667674397
                    </Link>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>

        {/* Organization Goals Section with Interactive Cards */}
        <Paper
          elevation={3}
          sx={{
            p: 4,
            mb: 6,
            background: `linear-gradient(45deg, ${themeColors.primary} 30%, ${themeColors.secondary} 90%)`,
            color: 'white',
            borderRadius: '16px',
            position: 'relative',
            overflow: 'hidden',
            '&::after': {
              content: '""',
              position: 'absolute',
              top: 0,
              right: 0,
              width: '200px',
              height: '200px',
              background: `radial-gradient(circle, ${themeColors.accent}30 0%, transparent 70%)`,
              borderRadius: '50%',
            }
          }}
          data-aos="zoom-in"
        >
          <Typography variant="h4" sx={{ mb: 3, fontWeight: 'bold' }}>
            Our Goals
          </Typography>
          <List>
            {[
              { icon: <BarChartIcon />, text: "Achieve sustained growth through customer satisfaction" },
              { icon: <SpeedIcon />, text: "Maintain highest standards of precision and quality" },
              { icon: <PrecisionManufacturingIcon />, text: "Continuously improve manufacturing excellence and efficiency" },
              { icon: <EmojiEventsIcon />, text: "Deliver world-class precision components" }
            ].map((item, index) => (
              <Zoom in style={{ transitionDelay: `${index * 150}ms` }} key={index}>
                <ListItem 
                  sx={{
                    '&:hover': {
                      transform: 'translateX(10px)',
                      transition: 'transform 0.3s ease',
                    }
                  }}
                >
                  <ListItemIcon sx={{ color: themeColors.accent }}>
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText primary={item.text} />
                </ListItem>
              </Zoom>
            ))}
          </List>
        </Paper>

        {/* Vision & Mission Section with Hover Effects */}
        <Grid container spacing={4} sx={{ mb: 8 }}>
          {[
            {
              title: "Our Vision",
              items: [
                "To become a leader in precision manufacturing, known for innovation and quality.",
                "Drive continuous improvement and technological advancement in our industry.",
                "Be the preferred choice for customers seeking high-precision engineering solutions."
              ]
            },
            {
              title: "Our Mission",
              items: [
                "Deliver exceptional value through high-quality precision components.",
                "Foster innovation and continuous improvement in our processes.",
                "Build lasting partnerships with our customers and stakeholders."
              ]
            }
          ].map((section, index) => (
            <Grid item xs={12} md={6} key={index} data-aos={index === 0 ? "fade-right" : "fade-left"}>
              <Paper
                elevation={0}
                sx={{
                  p: 4,
                  height: '100%',
                  transition: 'all 0.3s ease-in-out',
                  borderRadius: '16px',
                  position: 'relative',
                  overflow: 'hidden',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: '0 12px 24px rgba(0,0,0,0.1)',
                    '& .hover-gradient': {
                      opacity: 1,
                    }
                  }
                }}
              >
                <Box
                  className="hover-gradient"
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: `linear-gradient(135deg, ${themeColors.primary}10 0%, ${themeColors.secondary}10 100%)`,
                    opacity: 0,
                    transition: 'opacity 0.3s ease',
                    zIndex: 0,
                  }}
                />
                <Box sx={{ position: 'relative', zIndex: 1 }}>
                  <Typography variant="h4" sx={{ color: themeColors.primary, mb: 3, fontWeight: 'bold' }}>
                    {section.title}
                  </Typography>
                  <List>
                    {section.items.map((item, itemIndex) => (
                      <ListItem key={itemIndex}>
                        <ListItemText 
                          primary={item} 
                          sx={{ 
                            color: 'text.secondary',
                            '& .MuiTypography-root': {
                              transition: 'color 0.3s ease',
                            },
                            '&:hover .MuiTypography-root': {
                              color: themeColors.primary,
                            }
                          }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              </Paper>
            </Grid>
          ))}
        </Grid>

        {/* Capabilities Grid with Interactive Cards */}
        <Box sx={{ mb: 8 }} data-aos="fade-up">
          <Typography 
            variant="h4" 
            sx={{ 
              textAlign: 'center', 
              mb: 4, 
              color: themeColors.primary,
              fontWeight: 'bold'
            }}
          >
            Our Capabilities
          </Typography>
          <Grid container spacing={3}>
            {capabilities.map((capability, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Zoom in style={{ transitionDelay: `${index * 100}ms` }}>
                  <Paper
                    sx={{
                      p: 3,
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      textAlign: 'center',
                      transition: 'all 0.3s ease',
                      borderRadius: '12px',
                      '&:hover': {
                        transform: 'scale(1.05)',
                        boxShadow: '0 8px 16px rgba(0,0,0,0.1)',
                        '& .icon-wrapper': {
                          transform: 'rotateY(180deg)',
                        }
                      }
                    }}
                  >
                    <Box
                      className="icon-wrapper"
                      sx={{
                        mb: 2,
                        transition: 'transform 0.6s ease',
                        transformStyle: 'preserve-3d',
                      }}
                    >
                      {capability.icon}
                    </Box>
                    <Typography
                      variant="h6"
                      sx={{
                        color: themeColors.primary,
                        mb: 1,
                        fontWeight: 'bold'
                      }}
                    >
                      {capability.title}
                    </Typography>
                    <Typography color="text.secondary">
                      {capability.description}
                    </Typography>
                  </Paper>
                </Zoom>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Call to Action Section */}
        <Box 
          sx={{ 
            textAlign: 'center',
            mt: 6 
          }}
          data-aos="fade-up"
        >
          <Button
            variant="contained"
            size="large"
            sx={{
              bgcolor: themeColors.primary,
              color: 'white',
              px: 4,
              py: 1.5,
              fontSize: '1.1rem',
              borderRadius: '30px',
              '&:hover': {
                bgcolor: themeColors.secondary,
                transform: 'scale(1.05)',
                transition: 'all 0.3s ease',
              }
            }}
          >
            Contact Us Today
          </Button>
        </Box>
      </Container>
    </Box>
  );
};

export default About;
